"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Mail, MessageCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent-light/20 border border-accent rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.08),0_0_12px_hsl(45,95%,55%)]">
        <p className="text-foreground font-medium">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 bg-muted-dark-background/80 text-foreground-dark border-border/40">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Name</FormLabel>
            <FormControl>
              <Input placeholder="Your Name" {...field} className="bg-muted-dark-background/60 border-border/30 text-foreground placeholder:text-muted-foreground/70 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-[var(--duration-medium)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" {...field} className="bg-muted-dark-background/60 border-border/30 text-foreground placeholder:text-muted-foreground/70 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-[var(--duration-medium)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Message</FormLabel>
            <FormControl>
              <Input placeholder="Your message..." {...field} className="bg-muted-dark-background/60 border-border/30 text-foreground placeholder:text-muted-foreground/70 focus-visible:border-primary focus-visible:ring-primary/30 transition-all duration-[var(--duration-medium)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-foreground-accent hover:bg-accent-hover shadow-medium hover:shadow-strong hover:-translate-y-0.5 active:translate-y-0 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
        {isSubmitting ? <>
            <span className="animate-spin mr-2">⏳</span>Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
function ContactInfo() {
  const {
    ref: emailRef,
    isVisible: emailVisible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  const {
    ref: phoneRef,
    isVisible: phoneVisible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  return <div className="space-y-4 mt-6">
      <div ref={emailRef} className={`flex items-center gap-3 transition-all duration-[var(--duration-slow)] ${emailVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 border border-primary/30">
              <Mail className="w-5 h-5 text-primary" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Email us</p>
          </TooltipContent>
        </Tooltip>
        <Link to="mailto:contact@sambrandingandmarketing.com" className="text-foreground hover:text-accent transition-colors duration-[var(--duration-fast)]">
          contact@sambrandingandmarketing.com
        </Link>
      </div>

      <div ref={phoneRef} className={`flex items-center gap-3 transition-all duration-[var(--duration-slow)] delay-100 ${phoneVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/20 border border-secondary/30">
              <MessageCircle className="w-5 h-5 text-secondary" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Message us on WhatsApp</p>
          </TooltipContent>
        </Tooltip>
        <Link to="https://wa.me/971501813277" className="text-foreground hover:text-accent transition-colors duration-[var(--duration-fast)]">
          +971 50 181 3277
        </Link>
      </div>
    </div>;
}
export default function ContactInformation() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mapZoom, setMapZoom] = useState(1);
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    ref: cardRef,
    isVisible: cardVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const {
    ref: mapRef,
    isVisible: mapVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      if (sectionTop < windowHeight && sectionTop + sectionHeight > 0) {
        const progress = Math.max(0, Math.min(1, (windowHeight - sectionTop) / (windowHeight + sectionHeight)));
        setScrollProgress(progress);
        setMapZoom(1 + progress * 0.1);
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section ref={sectionRef} className="relative min-h-screen py-20 px-6 lg:px-12 overflow-hidden bg-dark-background">
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div ref={cardRef} className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${cardVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-16"}`}>
            <Card className="bg-muted-light-background/95 backdrop-blur-md shadow-[0_2px_4px_rgba(0,0,0,0.08),0_0_12px_hsl(45,95%,55%)] border border-border/20 rounded-xl p-8">
              <CardContent className="px-0">
                <h2 className="text-4xl font-display font-bold text-foreground mb-2">
                  Get In Touch
                </h2>
                <p className="text-muted-foreground mb-8">
                  We'd love to hear from you. Send us a message and we'll respond as soon as
                  possible.
                </p>

                <FormProvider className="space-y-6" formId="contact-form-contact-information" sectionName="contact-information" defaultValues={{
                name: "",
                email: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2,
                  maxLength: 50
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Send Message" successMessage="Thank you! We'll get back to you soon.">
                  <ContactForm />
                </FormProvider>

                <ContactInfo />
              </CardContent>
            </Card>
          </div>

          <div ref={mapRef} className={`relative h-[500px] lg:h-[600px] rounded-xl overflow-hidden shadow-[0_2px_4px_rgba(0,0,0,0.08),0_0_12px_hsl(45,95%,55%)] border border-border/20 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${mapVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-16"}`}>
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.1771449677584!2d55.2708295!3d25.1972415!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDExJzUwLjEiTiA1NcKwMTYnMTUuMCJF!5e0!3m2!1sen!2sae!4v1234567890123!5m2!1sen!2sae" width="100%" height="100%" style={{
            border: 0
          }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Sam Branding & Marketing Office Location" className="w-full h-full transition-transform duration-[var(--duration-slow)] ease-[var(--ease-smooth)]" />
          </div>
        </div>
      </div>
    </section>;
}